/**
 * Sign in with Google.
 *
 * Ported from `app/(auth)/sign-in/page.tsx`. The one piece of behaviour worth keeping in mind
 * is the error handling: **closing the Google popup is a normal user action**, not a failure,
 * and reporting "sign-in failed" for it trains people to distrust the message when something
 * really has gone wrong.
 */

import { Box, Button, Paper, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { useAuth } from "@/auth/AuthProvider";
import { BRAND_NAME } from "@/constants/brand";

/** Firebase codes for "the user changed their mind", which is not an error to report. */
const CANCELLED_CODES = new Set(["auth/popup-closed-by-user", "auth/cancelled-popup-request"]);

interface LocationState {
  from?: string;
}

export function SignInPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading, signIn } = useAuth();
  const [signingIn, setSigningIn] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const destination = (location.state as LocationState | null)?.from ?? "/";

  useEffect(() => {
    // The redirect lives in an effect, not in the sign-in handler, so it fires for both routes
    // into the signed-in state: completing the popup, and arriving here already signed in.
    if (!loading && user) void navigate(destination, { replace: true });
  }, [loading, user, navigate, destination]);

  async function handleSignIn() {
    setError(null);
    setSigningIn(true);
    try {
      await signIn();
      // The effect above redirects once auth state settles.
    } catch (caught) {
      const code =
        typeof caught === "object" && caught !== null
          ? (caught as { code?: string }).code
          : undefined;
      if (!code || !CANCELLED_CODES.has(code)) {
        setError("Sign-in failed. Please try again.");
        console.error("[sign-in] Google sign-in failed:", caught);
      }
      setSigningIn(false);
    }
  }

  return (
    <Box className="flex min-h-[60vh] items-center justify-center">
      <Paper className="w-full max-w-sm p-8 text-center">
        <Stack spacing={2.5}>
          <Stack spacing={1}>
            <Typography variant="overline" color="primary.dark">
              {BRAND_NAME}
            </Typography>
            <Typography variant="h5" component="h1">
              Sign in
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Access is granted by Jones + Palmer. Sign in with the Google account your J+P
              contact added for you.
            </Typography>
          </Stack>

          <Button
            variant="contained"
            fullWidth
            size="large"
            loading={signingIn || loading}
            onClick={() => void handleSignIn()}
          >
            Sign in with Google
          </Button>

          {error ? (
            <Typography role="alert" variant="caption" color="error" sx={{ fontWeight: 600 }}>
              {error}
            </Typography>
          ) : null}
        </Stack>
      </Paper>
    </Box>
  );
}
